import React, { FC, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Badge, COLOR as BADGE_COLOR, HIERARCHY } from "baseui/badge";
import { ClaimStatusTypes, ClaimFilterBy } from "kw/api/zeus";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { Link } from "kanopi-components/Link";
import { navigate } from "@reach/router";
import { TableView } from "kanopi-components/TableView/TableView";
import { useClaims } from "./hooks/useClaims";
import { useUser } from "kw/auth/useUser";
import { useFormatter } from "kw/util/useFormatter";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { ClaimsPageCreateClaimButton } from "./components/ClaimsPageCreateClaimButton";

export const ClaimsPage: FC = () => {
  const { claims, loading, fetchClaims, searchClaims } = useClaims();
  const { formatDate, formatString } = useFormatter();
  const user = useUser();

  const statusBadgeColor = {
    [ClaimStatusTypes.OPEN]: BADGE_COLOR.positive,
    [ClaimStatusTypes.CLOSED]: BADGE_COLOR.negative,
  };
  const handleSearch = (searchText: string, filterBy) => {
    searchClaims(searchText, filterBy);
  };
  const handlePagination = (search: string, filterBy, page: number) => {
    fetchClaims(search, filterBy ? filterBy : ClaimFilterBy.ALL, page);
  };

  const columnSchema = [
    {
      header: "Claim ID",
      id: "id",
      children: (row: TClaim) => (
        <Link
          onClick={() => navigate(`/admin/claims/${row.id}`)}
          data-testid={`rowId-${row.id}`}
        >
          {row.id}
        </Link>
      ),
      sortable: false,
    },
    {
      header: "Customer",
      id: "customer",
      children: (row: TClaim) =>
        formatString(
          `${row.claimProfile?.owner?.firstName} ${row.claimProfile?.owner?.lastName}`.trim()
        ),
      sortable: false,
    },
    {
      header: "Created",
      id: "created",
      children: (row: TClaim) => formatDate(row?.created),
      sortable: false,
    },
    {
      header: "Status",
      id: "status",
      children: (row: TClaim) => (
        <Badge
          hierarchy={HIERARCHY.secondary}
          content={formatString(row?.statusTypeDisplay)}
          color={statusBadgeColor[row?.statusType]}
        />
      ),
      sortable: false,
    },
  ];

  useEffect(() => {
    fetchClaims("", ClaimFilterBy.ALL, 1);
  }, []);

  return (
    <>
      <Helmet>
        <title>{`Claims - ${user?.organization?.name}`}</title>
      </Helmet>
      <VerticalSpacer>
        <HeadingActionsBar heading="Claims" />
        <TableView
          wrapInCard={true}
          headerActions={[<ClaimsPageCreateClaimButton />]}
          columnSchema={columnSchema}
          data={claims?.items || []}
          pageCount={claims?.pageCount || 1}
          onPageChange={handlePagination}
          currentPage={claims?.page || 1}
          loading={loading}
          onSearch={handleSearch}
          searchPlaceholder="Search by customer or claim id"
          type="CLAIM"
          showSearchOptions={true}
          searchOptions={[
            { label: "All", value: ClaimFilterBy.ALL },
            { label: "Open", value: ClaimFilterBy.OPEN },
            { label: "Closed", value: ClaimFilterBy.CLOSED },
          ]}
          filterPlaceholder="Filter by status"
        />
      </VerticalSpacer>
    </>
  );
};
